import {
  liqPrice,
  perpMargin,
  perpNotional,
  perpPnl,
  type PerpLeg,
} from "../lib/composePayoff";
import type { SelectedPerp } from "../types";
import {
  Field,
  NumberInput,
  ReadoutRow,
  SegmentToggle,
  Slider,
  readoutSectionStyle,
  sectionStyle,
} from "./sim-form-primitives";

const PERP_COLOR = "#a78bfa";

function formatUsd(n: number): string {
  if (!isFinite(n)) return "—";
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: Math.abs(n) < 10 ? 4 : 2,
  });
}

function signedUsd(n: number): string {
  if (!isFinite(n)) return "—";
  const neg = n < 0;
  const abs = Math.abs(n).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: Math.abs(n) < 10 ? 2 : 0,
  });
  return neg ? `−${abs}` : `+${abs}`;
}

export function PerpSimulateForm({
  market,
  leg,
  onChange,
}: {
  market: SelectedPerp;
  leg: PerpLeg;
  onChange: (next: PerpLeg) => void;
}) {
  const patch = (p: Partial<PerpLeg>) => {
    const next = { ...leg, ...p };
    next.liquidation = liqPrice(next.side, next.entry, next.leverage);
    onChange(next);
  };

  const notional = perpNotional(leg.size, leg.entry);
  const margin = perpMargin(leg.size, leg.entry, leg.leverage);
  const liq = leg.liquidation;
  const atMark = perpPnl(leg, market.markPx);
  const liqDistancePct =
    market.markPx > 0
      ? (Math.abs(market.markPx - liq) / market.markPx) * 100
      : 0;

  const maxLev = Math.max(1, market.maxLeverage || 1);
  const presets = Array.from(
    new Set([1, 2, 5, 10, 20, maxLev].filter((n) => n <= maxLev)),
  ).sort((a, b) => a - b);

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <div style={sectionStyle}>
        <Field label="SIDE">
          <SegmentToggle
            value={leg.side}
            onChange={(v) => patch({ side: v })}
            options={[
              { value: "long", label: "LONG", color: "var(--up)" },
              { value: "short", label: "SHORT", color: "var(--dn)" },
            ]}
          />
        </Field>
        <Field label="SIZE">
          <NumberInput
            value={leg.size}
            min={0}
            step={0.0001}
            suffix={market.coin}
            onChange={(n) => patch({ size: Math.max(0, n) })}
          />
        </Field>
        <Field label="ENTRY">
          <NumberInput
            value={leg.entry}
            min={0}
            step={0.01}
            suffix="USD"
            onChange={(n) => patch({ entry: Math.max(0, n) })}
          />
        </Field>
        <Field label={`LEVERAGE · ${leg.leverage}×`}>
          <Slider
            value={leg.leverage}
            onChange={(n) => patch({ leverage: n })}
            min={1}
            max={maxLev}
            step={1}
            color={PERP_COLOR}
          />
        </Field>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${presets.length}, 1fr)`,
            gap: 4,
          }}
        >
          {presets.map((n) => {
            const active = leg.leverage === n;
            return (
              <button
                key={n}
                type="button"
                onClick={() => patch({ leverage: n })}
                style={{
                  background: active ? `${PERP_COLOR}20` : "transparent",
                  color: active ? PERP_COLOR : "var(--fg-dim)",
                  border: `1px solid ${active ? PERP_COLOR : "var(--line-2)"}`,
                  fontFamily: "var(--f-mono)",
                  fontSize: 9,
                  letterSpacing: "0.08em",
                  padding: "4px 0",
                  cursor: "pointer",
                  borderRadius: 2,
                  fontWeight: active ? 600 : 400,
                }}
              >
                {n === maxLev && n > 20 ? `${n}× MAX` : `${n}×`}
              </button>
            );
          })}
        </div>
      </div>

      <div style={readoutSectionStyle}>
        <ReadoutRow label="NOTIONAL" value={formatUsd(notional)} />
        <ReadoutRow label="MARGIN" value={formatUsd(margin)} />
        <ReadoutRow
          label="LIQ PRICE"
          value={formatUsd(liq)}
          color="var(--sev-crit)"
        />
        <ReadoutRow
          label="LIQ DISTANCE"
          value={`${liqDistancePct.toFixed(2)}%`}
          color="var(--sev-crit)"
        />
      </div>

      <div
        style={{
          padding: "12px 14px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "var(--bg-0)",
          borderTop: "1px solid var(--line-2)",
        }}
      >
        <span
          style={{
            fontFamily: "var(--f-mono)",
            fontSize: 9,
            letterSpacing: "0.16em",
            color: "var(--fg-meta)",
            textTransform: "uppercase",
          }}
        >
          AT MARK
        </span>
        <span
          style={{
            fontFamily: "var(--f-mono)",
            fontSize: 16,
            fontVariantNumeric: "tabular-nums",
            fontWeight: 500,
            color: atMark >= 0 ? "var(--up)" : "var(--dn)",
          }}
        >
          {signedUsd(atMark)}
        </span>
      </div>
    </div>
  );
}
